<template>
  <section class="dash-panel dash-panel--journey">
    <div class="dash-panel__header">
      <div>
        <span class="dash-panel__eyebrow">第三层</span>
        <h2>事件链</h2>
      </div>
      <div class="dash-meta">
        <span>{{ displayAdvisorName }}</span>
        <span>{{ displayFamilyName }}</span>
        <span>{{ profile.title }}</span>
      </div>
    </div>

    <div class="journey-panel__body" @wheel="onBodyWheel" @scroll="updateSourcePickerPosition">
      <section class="dash-focus-card">
        <div class="dash-focus-card__top">
          <div>
            <span class="dash-panel__eyebrow">当前家庭</span>
            <h3>{{ displayFamilyName }}</h3>
            <p>{{ displayFamilyNote }}</p>
          </div>
          <span class="dash-badge">{{ displayChildLabel }}</span>
        </div>
        <div class="journey-metrics">
          <div class="journey-metrics__core" aria-label="核心沟通指标">
            <div class="journey-metric"><span>当前通话</span><strong>{{ eventsData.callCount }}</strong></div>
            <div class="journey-metric"><span>当前通话时长</span><strong>{{ metricCallDurationLabel }}</strong></div>
            <div class="journey-metric"><span>客户消息</span><strong>{{ eventsData.customerMsgCount }}</strong></div>
            <div class="journey-metric"><span>指导师消息</span><strong>{{ eventsData.teacherMsgCount }}</strong></div>
            <div class="journey-metric"><span>消息总量</span><strong>{{ eventsData.totalMsgCount }}</strong></div>
          </div>
          <div
            v-if="hasHistoricalOtherSaleCalls"
            class="journey-metrics__history-note"
            aria-label="历史通话提示"
          >
            存在历史其他销售通话记录
          </div>
          <div
            v-if="eventsData.violationCount || eventsData.reviewCount || eventsData.isTimeout"
            class="journey-metrics__risk"
            aria-label="风险指标"
          >
            <span v-if="eventsData.violationCount" class="journey-risk-pill journey-risk-pill--danger">
              建议违规 <strong>{{ eventsData.violationCount }}</strong>
            </span>
            <span v-if="eventsData.reviewCount" class="journey-risk-pill journey-risk-pill--review">
              待复核 <strong>{{ eventsData.reviewCount }}</strong>
            </span>
            <span v-if="eventsData.isTimeout" class="journey-risk-pill journey-risk-pill--timeout">
              超时率 <strong>{{ eventsData.timeoutRate }}%</strong>
            </span>
          </div>
        </div>
      </section>

      <section v-if="eventsData.violations.length || eventsData.reviews.length" class="qc-event-sections">
        <div v-if="eventsData.violations.length" class="qc-event-section qc-event-section--danger">
          <div class="qc-event-section__head">
            <span class="dash-panel__eyebrow">员工违规线索</span>
            <strong>{{ eventsData.violations.length }} 项</strong>
          </div>
          <article v-for="item in eventsData.violations" :key="item.taskId + item.findingId" :class="['qc-event-card', severityClass(item)]">
            <div class="qc-event-card__top">
              <div class="qc-event-card__title">
                <h4>{{ item.ruleName || item.ruleId || '未命名规则' }}</h4>
                <span v-if="item.ruleId">{{ item.ruleId }}</span>
              </div>
              <div class="qc-event-card__badges">
                <span :class="['qc-severity-badge', severityClass(item)]">{{ severityLabel(item) }}</span>
                <span v-if="item.confidence" class="qc-confidence-badge">置信度 {{ confidenceLabel(item.confidence) }}</span>
              </div>
            </div>

            <div class="qc-event-compact">
              <div class="qc-event-main">
                <span>违规行为</span>
                <p>{{ item.basis || '暂无判定说明' }}</p>
              </div>
              <div class="qc-event-quote">
                <span>员工原话</span>
                <div class="qc-evidence-list">
                  <div v-for="evidence in item.evidence || []" :key="evidenceKey(item, evidence)" class="qc-evidence">
                    <blockquote v-if="evidence.quote">{{ evidence.quote }}</blockquote>
                  </div>
                </div>
              </div>
            </div>

            <div class="qc-evidence-meta">
              <span>{{ sourceLabel(item.sourceType) }}</span>
              <span v-if="callTimeLabel(item)">{{ callTimeLabel(item) }}</span>
              <span v-if="primaryEvidencePosition(item)">{{ primaryEvidencePosition(item) }}</span>
              <span v-if="primaryEmployeeLabel(item)">{{ primaryEmployeeLabel(item) }}</span>
            </div>
            <details class="qc-trace" v-if="traceItems(item).length">
              <summary>追溯信息</summary>
              <div class="qc-trace__items">
                <span v-for="trace in traceItems(item)" :key="trace">{{ trace }}</span>
              </div>
            </details>
          </article>
        </div>

        <div v-if="eventsData.reviews.length" class="qc-event-section qc-event-section--review">
          <div class="qc-event-section__head">
            <span class="dash-panel__eyebrow">员工待复核线索</span>
            <strong>{{ eventsData.reviews.length }} 项</strong>
          </div>
          <article v-for="item in eventsData.reviews" :key="item.taskId + item.findingId" :class="['qc-event-card', severityClass(item)]">
            <div class="qc-event-card__top">
              <div class="qc-event-card__title">
                <h4>{{ item.ruleName || item.ruleId || '未命名规则' }}</h4>
                <span v-if="item.ruleId">{{ item.ruleId }}</span>
              </div>
              <div class="qc-event-card__badges">
                <span :class="['qc-severity-badge', severityClass(item)]">{{ severityLabel(item) }}</span>
                <span v-if="item.confidence" class="qc-confidence-badge">置信度 {{ confidenceLabel(item.confidence) }}</span>
              </div>
            </div>

            <div class="qc-event-compact">
              <div class="qc-event-main">
                <span>待核实点</span>
                <p>{{ item.basis || '暂无判定说明' }}</p>
              </div>
              <div class="qc-event-quote">
                <span>员工原话</span>
                <div class="qc-evidence-list">
                  <div v-for="evidence in item.evidence || []" :key="evidenceKey(item, evidence)" class="qc-evidence">
                    <blockquote v-if="evidence.quote">{{ evidence.quote }}</blockquote>
                  </div>
                </div>
              </div>
            </div>

            <div class="qc-evidence-meta">
              <span>{{ sourceLabel(item.sourceType) }}</span>
              <span v-if="callTimeLabel(item)">{{ callTimeLabel(item) }}</span>
              <span v-if="primaryEvidencePosition(item)">{{ primaryEvidencePosition(item) }}</span>
              <span v-if="primaryEmployeeLabel(item)">{{ primaryEmployeeLabel(item) }}</span>
            </div>
            <details class="qc-trace" v-if="traceItems(item).length">
              <summary>追溯信息</summary>
              <div class="qc-trace__items">
                <span v-for="trace in traceItems(item)" :key="trace">{{ trace }}</span>
              </div>
            </details>
          </article>
        </div>
      </section>


      <section v-if="eventChainData.items.length" class="event-timeline">
        <div class="event-timeline__summary">
          <div>
            <span class="dash-panel__eyebrow">关键动作</span>
            <h3>按时间还原沟通脉络</h3>
          </div>
          <span class="event-timeline__count">{{ eventChainData.items.length }} 条记录</span>
        </div>
        <div class="event-timeline__list">
          <article
            v-for="(item, idx) in eventChainData.items"
            :key="idx"
            :class="[
              'event-timeline__item',
              eventTypeClass(item),
              { 'event-timeline__item--source-active': activeSourceIndex === idx },
            ]"
            :style="{ '--tone': eventTypeTone(item) }"
          >
            <div class="event-timeline__rail">
              <span class="event-timeline__glyph" :aria-label="eventTypeLabel(item)">
                <svg
                  class="event-timeline__icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <template v-if="eventTypeKey(item) === 'risk'">
                    <path d="M10.3 4.4 2.8 17.2A2 2 0 0 0 4.5 20h15a2 2 0 0 0 1.7-2.8L13.7 4.4a2 2 0 0 0-3.4 0Z" />
                    <path d="M12 9v4" />
                    <path d="M12 17h.01" />
                  </template>
                  <template v-else-if="eventTypeKey(item) === 'call'">
                    <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.4 19.4 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2.1Z" />
                  </template>
                  <template v-else-if="eventTypeKey(item) === 'file'">
                    <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7Z" />
                    <path d="M14 2v5h5" />
                    <path d="M9 13h6" />
                    <path d="M9 17h4" />
                  </template>
                  <template v-else-if="eventTypeKey(item) === 'message'">
                    <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z" />
                  </template>
                  <template v-else>
                    <circle cx="12" cy="12" r="5" />
                    <path d="M12 3v2" />
                    <path d="M12 19v2" />
                    <path d="M3 12h2" />
                    <path d="M19 12h2" />
                  </template>
                </svg>
              </span>
              <span class="event-timeline__time">{{ item.time }}</span>
            </div>
              <div class="event-timeline__content">
                <div class="event-timeline__head">
                  <span class="event-timeline__type">{{ eventTypeLabel(item) }}</span>
                  <span v-if="isCall(item)" class="dash-badge dash-badge--ghost">{{ formatDuration(item.durationSeconds) }}</span>
                  <span v-if="item.category === 'FIRST_CALL'" class="dash-badge dash-badge--brand">首次沟通</span>
                  <div
                    v-if="hasSources(item)"
                    class="source-picker-zone"
                    @mouseenter="openSourcePicker(idx)"
                    @mouseleave="scheduleSourcePickerClose"
                    @focusin="openSourcePicker(idx)"
                    @focusout="scheduleSourcePickerClose"
                  >
                    <button
                      :ref="(element) => setSourceTriggerElement(idx, element)"
                      type="button"
                      class="source-picker__trigger"
                      :aria-expanded="activeSourceIndex === idx"
                      aria-haspopup="dialog"
                      @click="togglePinnedSourcePicker(idx)"
                    >
                      来源 {{ itemSources(item).length }}
                    </button>
                  </div>
                </div>
              <h4>{{ item.title }}</h4>
              <p v-if="item.desc">{{ item.desc }}</p>
              <div v-if="filteredMeta(item).length" class="dash-chip-row">
                <span v-for="m in filteredMeta(item)" :key="m" class="dash-badge dash-badge--ghost">{{ m }}</span>
              </div>
            </div>
          </article>
        </div>
      </section>
      <section
        v-else-if="eventChainData.status === 'ready'"
        class="event-chain-empty"
      >
        <p v-if="eventChainData.errorMessage">{{ eventChainData.errorMessage }}</p>
        <p v-else>暂无关键动作</p>
      </section>
      <div v-else-if="eventChainData.status === 'pending'" class="event-chain-loading">
        <span class="event-chain-loading__spinner"></span>
        事件链分析中...
      </div>
      <section v-else-if="eventChainData.status === 'failed'" class="event-chain-empty event-chain-empty--error">
        <p>{{ eventChainData.errorMessage || '事件链分析失败' }}</p>
      </section>
    </div>

    <Teleport to="body">
      <div
        ref="sourcePickerElement"
        v-if="activeSourceItem"
        class="source-picker"
        :style="sourcePickerStyle"
        role="dialog"
        aria-label="选择来源"
        @mouseenter="openSourcePicker(activeSourceIndex)"
        @mouseleave="scheduleSourcePickerClose"
        @focusin="openSourcePicker(activeSourceIndex)"
        @focusout="scheduleSourcePickerClose"
      >
        <div class="source-picker__head">
          <strong>关联来源</strong>
          <button type="button" class="source-picker__close" aria-label="关闭来源选择" @click="closeSourcePicker">×</button>
        </div>
        <button
          v-for="source in sortedSources(itemSources(activeSourceItem))"
          :key="sourceKey(source)"
          type="button"
          class="source-picker__row"
          @click="selectSource(source)"
        >
          <span class="source-picker__kind">{{ sourceTypeLabel(source) }}</span>
          <span class="source-picker__time">{{ sourceTimeLabel(source) }}</span>
          <span class="source-picker__detail">{{ sourceDescription(source) }}</span>
        </button>
      </div>
    </Teleport>

    <div v-if="callModal.open" class="call-detail-modal" role="dialog" aria-modal="true" aria-labelledby="call-detail-title" @click.self="closeCallModal">
      <section class="call-detail-modal__content">
        <div class="call-detail-modal__head">
          <div>
            <span class="dash-panel__eyebrow">通话来源</span>
            <h3 id="call-detail-title">基础通话详情</h3>
          </div>
          <button type="button" class="call-detail-modal__close" aria-label="关闭通话详情" @click="closeCallModal">×</button>
        </div>
        <dl class="call-detail-modal__list">
          <div><dt>通话时间</dt><dd>{{ displayCallValue(callModal.detail.callTime) }}</dd></div>
          <div><dt>通话时长</dt><dd>{{ displayCallValue(callModal.detail.duration) }}</dd></div>
          <div><dt>指导师</dt><dd>{{ displayCallValue(callModal.detail.originator) }}</dd></div>
          <div><dt>标识</dt><dd>{{ displayCallValue(callModal.detail.identifier) }}</dd></div>
        </dl>
        <section v-if="callModal.detail.transcriptAvailable" class="call-transcript" aria-label="通话转录">
          <div class="call-transcript__head">
            <span class="dash-panel__eyebrow">通话转录</span>
            <span v-if="callModal.detail.transcriptSegments.length">{{ callModal.detail.transcriptSegments.length }} 个片段</span>
          </div>
          <div v-if="callModal.detail.transcriptSegments.length" class="call-transcript__list">
            <article v-for="segment in callModal.detail.transcriptSegments" :key="segment.segmentId" class="call-transcript__segment">
              <div class="call-transcript__meta">
                <strong>{{ segment.speaker || '未知说话人' }}</strong>
                <span>{{ transcriptTimeLabel(segment) }}</span>
              </div>
              <p>{{ segment.content }}</p>
            </article>
          </div>
          <p v-else class="call-transcript__raw">{{ callModal.detail.transcript }}</p>
        </section>
        <div class="call-detail-modal__actions">
           <button
             type="button"
             class="call-detail-modal__action"
            :disabled="!callModal.detail.chatMessageId"
            :title="callModal.detail.chatMessageId ? '定位到对应的企微音视频通话消息' : callModal.detail.chatTargetReason"
            @click="jumpToChatMessage"
          >
             查看企微聊天位置
           </button>
        </div>
        <p v-if="!callModal.detail.chatMessageId && callModal.detail.chatTargetReason" class="call-detail-modal__notice">
          {{ callModal.detail.chatTargetReason }}
        </p>
        <p v-if="callModal.loading" class="call-detail-modal__notice">正在获取通话详情...</p>
        <p v-else-if="callModal.error" class="call-detail-modal__notice">{{ callModal.error }}</p>
      </section>
    </div>
  </section>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { getCallDetail, getEvents, getEventChain } from '@/api'

const props = defineProps({
  selectedAdvisor: { type: Object, required: true },
  selectedFamily: { type: Object, required: true },
  profile: { type: Object, required: true },
  familySignals: { type: Array, required: true },
  campPeriodName: { type: String, default: '' },
  selectedCustomerId: { type: String, default: '' },
  selectedSaleId: { type: String, default: '' },
  selectedTeacherName: { type: String, default: '' },
  selectedParent: { type: Object, default: null },
})

const emit = defineEmits(['chat-source-selected', 'call-source-selected'])

const eventsData = reactive({
  hasHistoryTeachers: false,
  teacherCount: 1,
  callCount: 0,
  callDurationMinutes: 0,
  historyCallCount: 0,
  historyCallDurationMinutes: 0,
  customerMsgCount: 0,
  teacherMsgCount: 0,
  totalMsgCount: 0,
  timeoutMsgCount: 0,
  isTimeout: false,
  timeoutRate: 0,
  violationCount: 0,
  reviewCount: 0,
  violations: [],
  reviews: [],
})

function resetEventsData() {
  eventsData.hasHistoryTeachers = false
  eventsData.teacherCount = 1
  eventsData.callCount = 0
  eventsData.callDurationMinutes = 0
  eventsData.historyCallCount = 0
  eventsData.historyCallDurationMinutes = 0
  eventsData.customerMsgCount = 0
  eventsData.teacherMsgCount = 0
  eventsData.totalMsgCount = 0
  eventsData.timeoutMsgCount = 0
  eventsData.isTimeout = false
  eventsData.timeoutRate = 0
  eventsData.violationCount = 0
  eventsData.reviewCount = 0
  eventsData.violations = []
  eventsData.reviews = []
}

async function fetchEvents() {
  resetEventsData()
  if (!props.campPeriodName || !props.selectedCustomerId) return
  try {
    const params = {
      campPeriodName: props.campPeriodName,
      customerId: props.selectedCustomerId,
    }
    if (props.selectedSaleId) params.saleId = props.selectedSaleId
    const res = await getEvents(params)
    if (res && typeof res === 'object' && !Array.isArray(res)) {
      const data = res.code === 200 ? res.data : res
      if (data) {
        eventsData.hasHistoryTeachers = Boolean(data.hasHistoryTeachers)
        eventsData.teacherCount = data.teacherCount || 1
        eventsData.callCount = data.callCount ?? 0
        eventsData.callDurationMinutes = data.callDurationMinutes ?? 0
        eventsData.historyCallCount = data.historyCallCount ?? 0
        eventsData.historyCallDurationMinutes = data.historyCallDurationMinutes ?? 0
        eventsData.customerMsgCount = data.customerMsgCount ?? 0
        eventsData.teacherMsgCount = data.teacherMsgCount ?? 0
        eventsData.totalMsgCount = data.totalMsgCount ?? 0
        eventsData.timeoutMsgCount = data.timeoutMsgCount ?? 0
        eventsData.isTimeout = data.timeout ?? data.isTimeout ?? false
        eventsData.timeoutRate = data.timeoutRate ?? 0
        eventsData.violationCount = data.violationCount ?? 0
        eventsData.reviewCount = data.reviewCount ?? 0
        eventsData.violations = Array.isArray(data.violations) ? data.violations : []
        eventsData.reviews = Array.isArray(data.reviews) ? data.reviews : []
      }
    }
  } catch (err) {
    console.error('获取事件数据失败', err)
  }
}

const hasHistoricalOtherSaleCalls = computed(() =>
  eventsData.historyCallCount > eventsData.callCount ||
  eventsData.historyCallDurationMinutes > eventsData.callDurationMinutes
)

// Event chain watch is below (combined with fetchEvents)

// ---- Event Chain (AI) ----
const eventChainData = reactive({
  status: 'idle',
  taskId: null,
  errorMessage: '',
  items: [],
})
let eventChainAbort = false
let pollTimer = null
let eventChainRequestSeq = 0
let sourceCloseTimer = null
let callDetailRequestSeq = 0
const activeSourceIndex = ref(null)
const sourcePickerPinned = ref(false)
const sourceTriggerElements = new Map()
const sourcePickerPosition = ref({ left: 0, top: 0 })
const sourcePickerElement = ref(null)
const callModal = reactive({
  open: false,
  source: null,
  loading: false,
  error: '',
  detail: {
    callTime: '', duration: '', originator: '', identifier: '', transcriptAvailable: false,
    transcript: '', transcriptSegments: [], chatMessageId: '', chatTargetReason: '',
  },
})

function stopEventChainPoll() {
  eventChainAbort = true
  if (pollTimer) { clearTimeout(pollTimer); pollTimer = null }
}

async function fetchEventChain() {
  stopEventChainPoll()
  eventChainAbort = false
  const requestSeq = ++eventChainRequestSeq
  eventChainData.status = 'idle'
  eventChainData.taskId = null
  eventChainData.errorMessage = ''
  eventChainData.items = []

  if (!props.campPeriodName || !props.selectedCustomerId) return
  try {
    const params = {
      campPeriodName: props.campPeriodName,
      customerId: props.selectedCustomerId,
    }
    if (props.selectedSaleId) params.saleId = props.selectedSaleId
    const res = await getEventChain(params)
    const data = res && typeof res === 'object' && !Array.isArray(res) ? (res.code === 200 ? res.data : res) : null
    if (!data || isStaleEventChainRequest(requestSeq)) return

    if (applyEventChainResult(data, requestSeq)) {
      return
    } else if (data.status === 'pending' && data.taskId) {
      eventChainData.status = 'pending'
      eventChainData.taskId = data.taskId
      pollEventChain(data.taskId, requestSeq, params)
    }
  } catch (err) {
    console.error('获取事件链失败', err)
  }
}

function isStaleEventChainRequest(requestSeq) {
  return eventChainAbort || requestSeq !== eventChainRequestSeq
}

function applyEventChainResult(data, requestSeq) {
  if (!data || isStaleEventChainRequest(requestSeq)) return true
  if (data.status === 'ready') {
    eventChainData.status = 'ready'
    eventChainData.errorMessage = data.errorMessage || ''
    eventChainData.items = Array.isArray(data.items) ? data.items : []
    return true
  }
  if (data.status === 'failed') {
    eventChainData.status = 'failed'
    eventChainData.errorMessage = data.errorMessage || '分析失败'
    eventChainData.items = []
    return true
  }
  return false
}

async function loadCompletedEventChain(params, requestSeq) {
  const res = await getEventChain(params)
  const data = res && typeof res === 'object' && !Array.isArray(res) ? (res.code === 200 ? res.data : res) : null
  if (applyEventChainResult(data, requestSeq)) return
  if (data?.status === 'pending' && data.taskId) {
    eventChainData.status = 'pending'
    eventChainData.taskId = data.taskId
    pollEventChain(data.taskId, requestSeq, params)
  }
}

async function pollEventChain(taskId, requestSeq, params) {
  let attempts = 0
  const maxAttempts = 60
  const interval = 3000

  async function tick() {
    if (isStaleEventChainRequest(requestSeq) || attempts >= maxAttempts) return
    attempts++
    try {
      const { default: request } = await import('@/utils/request')
      const res = await request({ url: `/ai/tasks/${taskId}`, method: 'get' })
      const task = res && typeof res === 'object' ? (res.code === 200 ? res.data : res) : null
      if (!task || isStaleEventChainRequest(requestSeq)) return

      if (task.status === 'COMPLETED') {
        await loadCompletedEventChain(params, requestSeq)
        return
      }
      if (task.status === 'FAILED') {
        if (isStaleEventChainRequest(requestSeq)) return
        eventChainData.status = 'failed'
        eventChainData.errorMessage = task.errorMessage || '分析失败'
        return
      }
      // Still RUNNING — poll again
      if (!isStaleEventChainRequest(requestSeq)) pollTimer = setTimeout(tick, interval)
    } catch (err) {
      console.error('轮询事件链任务失败', err)
      if (attempts < maxAttempts && !isStaleEventChainRequest(requestSeq)) pollTimer = setTimeout(tick, interval)
    }
  }
  tick()
}

watch(() => [props.campPeriodName, props.selectedCustomerId, props.selectedSaleId], () => {
  closeSourcePicker()
  closeCallModal()
  fetchEvents()
  fetchEventChain()
}, { immediate: true })

function hasSources(item) {
  return itemSources(item).length > 0
}

function itemSources(item) {
  if (!Array.isArray(item?.sources)) return []
  return item.sources.filter(source => source && (source.type === 'chat' || source.type === 'call'))
}

function openSourcePicker(index, focusPicker = false) {
  if (index == null) return
  if (sourceCloseTimer) clearTimeout(sourceCloseTimer)
  activeSourceIndex.value = index
  nextTick(() => {
    updateSourcePickerPosition()
    if (focusPicker) sourcePickerElement.value?.querySelector('.source-picker__close')?.focus()
  })
}

function togglePinnedSourcePicker(index) {
  if (activeSourceIndex.value === index && sourcePickerPinned.value) {
    closeSourcePicker()
    return
  }
  openSourcePicker(index)
  sourcePickerPinned.value = true
}

function scheduleSourcePickerClose() {
  if (sourcePickerPinned.value) return
  if (sourceCloseTimer) clearTimeout(sourceCloseTimer)
  sourceCloseTimer = setTimeout(closeSourcePicker, 160)
}

function closeSourcePicker() {
  if (sourceCloseTimer) clearTimeout(sourceCloseTimer)
  sourceCloseTimer = null
  activeSourceIndex.value = null
  sourcePickerPinned.value = false
}

const activeSourceItem = computed(() => {
  const index = activeSourceIndex.value
  return index == null ? null : eventChainData.items[index] || null
})

const sourcePickerStyle = computed(() => ({
  left: `${sourcePickerPosition.value.left}px`,
  top: `${sourcePickerPosition.value.top}px`,
}))

function setSourceTriggerElement(index, element) {
  if (element) {
    sourceTriggerElements.set(index, element)
  } else {
    sourceTriggerElements.delete(index)
  }
}

function updateSourcePickerPosition() {
  const trigger = sourceTriggerElements.get(activeSourceIndex.value)
  if (!trigger) return

  const rect = trigger.getBoundingClientRect()
  const pickerRect = sourcePickerElement.value?.getBoundingClientRect()
  const pickerWidth = pickerRect?.width || Math.min(320, window.innerWidth - 24)
  const pickerHeight = pickerRect?.height || 0
  const mobile = window.innerWidth <= 720
  const rightLeft = rect.right + 8
  const leftLeft = rect.left - pickerWidth - 8

  if (mobile) {
    const belowTop = rect.bottom + 6
    const aboveTop = rect.top - pickerHeight - 6
    sourcePickerPosition.value = {
      left: Math.max(12, Math.min(rect.right - pickerWidth, window.innerWidth - pickerWidth - 12)),
      top: belowTop + pickerHeight <= window.innerHeight - 12 ? belowTop : Math.max(12, aboveTop),
    }
    return
  }

  sourcePickerPosition.value = {
    left: rightLeft + pickerWidth <= window.innerWidth - 12 ? rightLeft : Math.max(12, leftLeft),
    top: Math.max(pickerHeight / 2 + 12, Math.min(rect.top + rect.height / 2, window.innerHeight - pickerHeight / 2 - 12)),
  }
}

function sourceOccurrenceTime(source) {
  return source?.type === 'call' ? source.callTime : source?.msgtime
}

function sortedSources(sources) {
  return [...sources].sort((left, right) => {
    const leftTime = new Date(sourceOccurrenceTime(left)).getTime()
    const rightTime = new Date(sourceOccurrenceTime(right)).getTime()
    return (Number.isNaN(leftTime) ? 0 : leftTime) - (Number.isNaN(rightTime) ? 0 : rightTime)
  })
}

function sourceKey(source) {
  return [source?.type, source?.msgid, source?.callId, sourceOccurrenceTime(source), source?.preview].filter(Boolean).join(':')
}

function sourceTypeLabel(source) {
  return source?.type === 'call' ? '通话' : '聊天'
}

function sourceTimeLabel(source) {
  return formatDateTime(sourceOccurrenceTime(source)) || '时间未知'
}

function sourceDescription(source) {
  if (source?.type === 'call') {
    return [source.saleName || source.guideName || source.guideIdentity || source.guideId || '指导师未知', formatDuration(source.durationSeconds ?? source.duration)].filter(Boolean).join(' · ')
  }
  return [source.senderName || source.sender || source.msgtype || source.messageType || '聊天消息', source.preview || source.content || '暂无预览'].filter(Boolean).join(' · ')
}

function fallbackCallDetail(source) {
  const chatSource = source?.type === 'chat'
  const originator = chatSource
    ? (source?.sender === 'teacher' ? props.selectedTeacherName : displayFamilyName.value)
    : (props.selectedTeacherName || source?.saleName || source?.guideName)
  return {
    callTime: formatDateTime(source?.callTime || source?.msgtime),
    duration: formatDuration(source?.durationSeconds ?? source?.duration),
    originator,
    identifier: chatSource ? '' : (source?.callId || source?.identifier || source?.guideId || ''),
    chatMessageId: source?.chatMessageId || source?.msgid || '',
    chatTargetReason: source?.chatMessageId || source?.msgid ? '' : '未找到对应的企微音视频通话消息',
  }
}

function displayCallValue(value) {
  return value == null || String(value).trim() === '' ? '-' : value
}

function selectSource(source) {
  closeSourcePicker()
  if (source?.type !== 'call' && !(source?.type === 'chat' && source?.msgtype === 'av_call')) {
    emit('chat-source-selected', source)
    return
  }
  openCallModal(source)
}

function closeCallModal() {
  callDetailRequestSeq++
  callModal.open = false
  callModal.source = null
  callModal.loading = false
  callModal.error = ''
  callModal.detail.chatMessageId = ''
  callModal.detail.chatTargetReason = ''
  callModal.detail.transcriptAvailable = false
  callModal.detail.transcript = ''
  callModal.detail.transcriptSegments = []
}

function applyCallDetail(detail) {
  callModal.detail = {
    callTime: formatDateTime(detail.callTime || detail.callStartedAt || detail.startedAt) || callModal.detail.callTime,
    duration: detail.durationLabel || formatDuration(detail.durationSeconds ?? detail.duration) || callModal.detail.duration,
    originator: props.selectedTeacherName || detail.saleName || detail.guideName || detail.guideIdentity || detail.guideId || callModal.detail.originator,
    identifier: detail.callId || detail.identifier || detail.guideId || callModal.detail.identifier,
    transcriptAvailable: Boolean(detail.transcriptAvailable && (detail.transcript || detail.transcriptSegments?.length)),
    transcript: detail.transcript || '',
    transcriptSegments: Array.isArray(detail.transcriptSegments) ? detail.transcriptSegments : [],
    chatMessageId: detail.chatMessageId || callModal.detail.chatMessageId,
    chatTargetReason: detail.chatTargetReason || (!detail.chatMessageId && !callModal.detail.chatMessageId
      ? '未找到对应的企微音视频通话消息' : ''),
  }
}

function transcriptTimeLabel(segment) {
  if (segment?.startSeconds == null && segment?.endSeconds == null) return '时间未知'
  const start = formatOffset(segment.startSeconds ?? 0)
  const end = segment.endSeconds == null ? '' : ` - ${formatOffset(segment.endSeconds)}`
  return `${start}${end}`
}

function jumpToChatMessage() {
  if (!callModal.detail.chatMessageId) return
  emit('chat-source-selected', { ...callModal.source, msgid: callModal.detail.chatMessageId })
  closeCallModal()
}

async function openCallModal(source) {
  const requestSeq = ++callDetailRequestSeq
  callModal.open = true
  callModal.source = source
  callModal.loading = false
  callModal.error = ''
  callModal.detail = fallbackCallDetail(source)
  callModal.detail.transcriptAvailable = Boolean(source?.transcriptAvailable && (source?.transcript || source?.transcriptSegments?.length))
  callModal.detail.transcript = source?.transcript || ''
  callModal.detail.transcriptSegments = Array.isArray(source?.transcriptSegments) ? source.transcriptSegments : []

  if (source?.type !== 'call' || !source?.callId || !props.selectedCustomerId || !props.selectedSaleId || !props.campPeriodName) return

  callModal.loading = true
  try {
    const res = await getCallDetail({
      customerId: props.selectedCustomerId,
      saleId: props.selectedSaleId,
      campPeriodName: props.campPeriodName,
      callId: source.callRecordId || source.callId,
    })
    const detail = res && typeof res === 'object' ? (res.code === 200 ? res.data : res) : null
    if (requestSeq !== callDetailRequestSeq || !callModal.open || callModal.source !== source) return
    if (detail?.found === false || !detail) {
      callModal.error = '未找到更多通话详情。'
      return
    }
    applyCallDetail(detail)
  } catch (err) {
    if (requestSeq === callDetailRequestSeq && callModal.open && callModal.source === source) callModal.error = '通话详情暂时无法获取。'
  } finally {
    if (requestSeq === callDetailRequestSeq && callModal.open && callModal.source === source) callModal.loading = false
  }
}

function onDocumentClick(event) {
  if (activeSourceIndex.value !== null
    && !event.target.closest('.source-picker-zone')
    && !event.target.closest('.source-picker')) closeSourcePicker()
}

function onDocumentKeydown(event) {
  if (event.key === 'ArrowDown' && activeSourceIndex.value !== null
    && event.target === sourceTriggerElements.get(activeSourceIndex.value)) {
    event.preventDefault()
    openSourcePicker(activeSourceIndex.value, true)
    return
  }
  if (event.key !== 'Escape') return
  if (activeSourceIndex.value !== null) closeSourcePicker()
  else if (callModal.open) closeCallModal()
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick)
  document.addEventListener('keydown', onDocumentKeydown)
  window.addEventListener('resize', updateSourcePickerPosition)
})

onBeforeUnmount(() => {
  closeSourcePicker()
  closeCallModal()
  stopEventChainPoll()
  document.removeEventListener('click', onDocumentClick)
  document.removeEventListener('keydown', onDocumentKeydown)
  window.removeEventListener('resize', updateSourcePickerPosition)
})

// ---- Timeline helpers ----
function isCall(item) {
  return item.category === 'CALL_RECORD' || item.category === 'FIRST_CALL'
}

function filteredMeta(item) {
  if (!item.meta || !item.meta.length) return []
  if (isCall(item) && item.durationSeconds != null && item.durationSeconds > 0) {
    return item.meta.filter(m => !/通话\s*[:：]?\s*\d+\s*[分分钟]\s*\d*\s*[秒]?/.test(m))
  }
  return item.meta
}

function toneColor(item) {
  if (item.tone === 'danger') return 'var(--danger)'
  if (item.tone === 'warning') return 'var(--warn)'
  return 'var(--brand)'
}

function eventTypeKey(item) {
  const category = item.category || ''
  const title = `${item.title || ''}${item.desc || ''}${(item.meta || []).join('')}`
  if (item.tone === 'danger' || /不满|抱怨|退费|风险|违规|复核/.test(title)) return 'risk'
  if (/文件|方案|问卷|表单|量表|测试|资料/.test(title)) return 'file'
  if (category === 'FIRST_CALL' || category === 'CALL_RECORD' || /通话|电话|沟通完成|首次沟通/.test(title)) return 'call'
  if (category.includes('CHAT') || title.includes('消息')) return 'message'
  return 'milestone'
}

function eventTypeTone(item) {
  return {
    risk: 'var(--danger)',
    call: 'var(--brand)',
    file: 'var(--warn)',
    message: '#527f8f',
    milestone: '#6c7f75',
  }[eventTypeKey(item)] || toneColor(item)
}

function eventTypeClass(item) {
  return `event-timeline__item--${eventTypeKey(item)}`
}

function eventTypeLabel(item) {
  return {
    risk: '风险情绪',
    call: '通话记录',
    file: '方案/文件',
    message: '消息互动',
    milestone: '关键动作',
  }[eventTypeKey(item)] || '关键动作'
}

function onBodyWheel(event) {
  const el = event.currentTarget
  if (!el) return

  const canScroll = el.scrollHeight > el.clientHeight + 1
  const atTop = el.scrollTop <= 0
  const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1
  const scrollingUp = event.deltaY < 0
  const scrollingDown = event.deltaY > 0

  if (canScroll && ((scrollingUp && !atTop) || (scrollingDown && !atBottom))) {
    event.stopPropagation()
    return
  }

  // let browser handle scroll chaining naturally
}

const displayAdvisorName = computed(() => {
  if (props.selectedTeacherName) return props.selectedTeacherName
  return '请选择指导师'
})
const displayFamilyName = computed(() => {
  if (props.selectedParent?.parentName) return props.selectedParent.parentName
  return '请选择家长'
})
const displayChildLabel = computed(() => {
  const childName = props.selectedParent?.childName
  const childGrade = props.selectedParent?.childGrade
  if (childName || childGrade) return [childName, childGrade].filter(Boolean).join(' · ')
  return '未选择'
})
const metricCallDurationLabel = computed(() => formatCompactMinutes(eventsData.callDurationMinutes))
const displayFamilyNote = computed(() => {
  if (props.selectedParent?.qcStatus === 'suggested_violation') {
    return '该家庭存在建议违规质检事件，请优先查看下方规则、行为和证据。'
  }
  if (props.selectedParent?.qcStatus === 'needs_review') {
    return '该家庭存在待复核质检事件，请核实规则触发背景和原始证据。'
  }
  if (props.selectedParent?.parentName) {
    return '该家庭当前未发现建议违规质检事件，可继续查看沟通触达情况。'
  }
  return '请选择一个家长查看事件链、质检线索和关键沟通动作。'
})
function sourceLabel(sourceType) {
  if (sourceType === 'voice_call') return '来源：通话记录'
  if (sourceType === 'wechat_chat') return '来源：企微聊天'
  return '质检'
}

function evidenceKey(item, evidence) {
  return [
    item.taskId,
    item.findingId,
    evidence.segmentId,
    evidence.msgid,
    evidence.startSeconds,
    evidence.quote,
  ].filter((part) => part !== undefined && part !== null && part !== '').join(':')
}

function evidencePositionLabel(evidence) {
  if (!evidence) return ''
  if (evidence.segmentId) {
    return callOffsetLabel(evidence.startSeconds, evidence.endSeconds)
  }
  if (evidence.msgtime) return `消息时间：${formatDateTime(evidence.msgtime)}`
  if (evidence.msgid) return evidence.msgid
  return ''
}

function employeeLabel(evidence) {
  if (!evidence) return ''
  const confidence = evidence.attributionConfidence ? `身份确认：${confidenceLabel(evidence.attributionConfidence)}` : ''
  return ['发言方：员工', confidence].filter(Boolean).join('，')
}

function primaryEvidence(item) {
  return Array.isArray(item?.evidence) ? item.evidence[0] : null
}

function primaryEvidencePosition(item) {
  return evidencePositionLabel(primaryEvidence(item))
}

function primaryEmployeeLabel(item) {
  return employeeLabel(primaryEvidence(item))
}

function reviewStatusLabel(status) {
  if (status === 'suggested_violation') return '建议违规'
  if (status === 'needs_review') return '待复核'
  return status || '质检结果'
}

function confidenceLabel(value) {
  if (value === 'high') return '高'
  if (value === 'medium') return '中'
  if (value === 'low') return '低'
  return value || ''
}

function severityLabel(item) {
  return item.severitySuggestion || reviewStatusLabel(item.reviewStatus)
}

function severityClass(item) {
  const severity = item.severitySuggestion || ''
  if (severity.includes('红')) return 'is-red'
  if (severity.includes('黄') || item.reviewStatus === 'needs_review') return 'is-yellow'
  return 'is-neutral'
}

function callTimeLabel(item) {
  if (item.sourceType !== 'voice_call') return ''
  const start = formatDateTime(item.callStartedAt)
  const end = formatDateTime(item.callEndedAt)
  if (start && end) return `通话时间：${start} - ${end}`
  if (start) return `通话开始：${start}`
  if (item.durationSeconds != null) return `通话时长：${formatDuration(item.durationSeconds)}`
  return ''
}

function callOffsetLabel(startSeconds, endSeconds) {
  if (startSeconds == null && endSeconds == null) return ''
  if (startSeconds != null && endSeconds != null) {
    return `证据位置：通话后 ${formatOffset(startSeconds)}-${formatOffset(endSeconds)}`
  }
  return `证据位置：通话后 ${formatOffset(startSeconds ?? endSeconds)}`
}

function traceItems(item) {
  const firstEvidence = Array.isArray(item.evidence) ? item.evidence[0] : null
  return [
    item.sourceRecordId ? `记录ID：${item.sourceRecordId}` : '',
    item.callId && item.callId !== item.sourceRecordId ? `通话ID：${item.callId}` : '',
    item.taskId ? `任务ID：${item.taskId}` : '',
    item.findingId ? `发现项ID：${item.findingId}` : '',
    firstEvidence?.segmentId ? `片段ID：${firstEvidence.segmentId}` : '',
    firstEvidence?.speaker ? `原始说话人：${firstEvidence.speaker}` : '',
    firstEvidence?.msgid ? `消息ID：${firstEvidence.msgid}` : '',
  ].filter(Boolean)
}

function formatOffset(value) {
  const total = Math.max(0, Math.floor(Number(value) || 0))
  const minutes = String(Math.floor(total / 60)).padStart(2, '0')
  const seconds = String(total % 60).padStart(2, '0')
  return `${minutes}:${seconds}`
}

function formatDuration(seconds) {
  const total = Math.max(0, Math.round(Number(seconds) || 0))
  const minutes = Math.floor(total / 60)
  const remain = total % 60
  if (minutes <= 0) return `${remain} 秒`
  if (remain === 0) return `${minutes} 分钟`
  return `${minutes} 分 ${remain} 秒`
}

function formatCompactMinutes(value) {
  const minutes = Number(value) || 0
  if (minutes <= 0) return '0分'
  const totalSeconds = Math.round(minutes * 60)
  const hours = Math.floor(totalSeconds / 3600)
  const remainMinutes = Math.round((totalSeconds % 3600) / 60)
  if (hours > 0 && remainMinutes > 0) return `${hours}时${remainMinutes}分`
  if (hours > 0) return `${hours}时`
  if (minutes >= 10) return `${Math.round(minutes)}分`
  return `${Math.round(totalSeconds / 60)}分`
}

function formatDateTime(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value).replace('T', ' ').replace(/\\.\\d{3}Z?$/, '')
  const pad = (num) => String(num).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}
</script>


<style scoped>
.qc-event-sections {
  display: grid;
  gap: 8px;
  margin-top: 10px;
}

.journey-panel__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior-y: auto;
  padding-right: 0;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}

.journey-panel__body::-webkit-scrollbar {
  display: none;
}

.journey-metrics {
  display: grid;
  gap: 7px;
  margin-top: 10px;
}

.journey-panel__body > .dash-focus-card {
  position: sticky;
  top: 0;
  z-index: 8;
  margin-bottom: 12px;
  background:
    linear-gradient(180deg, rgba(255, 250, 244, 0.97), rgba(255, 250, 244, 0.92)),
    radial-gradient(circle at 8% 0%, rgba(41, 107, 115, 0.08), transparent 34%);
  box-shadow:
    0 16px 34px rgba(37, 56, 50, 0.12),
    0 1px 0 rgba(255, 255, 255, 0.72) inset;
}

.journey-metrics__core {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 6px;
}

.journey-metric {
  min-width: 0;
  padding: 8px 9px;
  border-radius: 12px;
  border: 1px solid var(--line);
  background: rgba(255, 250, 244, 0.72);
}

.journey-metric span {
  display: block;
  color: var(--ink-soft);
  font-size: 0.64rem;
  font-weight: 700;
  line-height: 1.25;
  min-height: 1.6em;
  white-space: normal;
}

.journey-metric strong {
  display: block;
  margin-top: 4px;
  color: var(--ink);
  font-size: 0.86rem;
  line-height: 1.1;
  overflow-wrap: anywhere;
}

.journey-metrics__risk {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.journey-metrics__history-note {
  color: color-mix(in srgb, var(--ink-soft) 88%, var(--ink));
  font-size: 0.66rem;
  font-weight: 700;
  line-height: 1.35;
  padding-left: 2px;
}

.journey-risk-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 26px;
  padding: 4px 9px;
  border-radius: 999px;
  font-size: 0.66rem;
  font-weight: 800;
  white-space: nowrap;
}

.journey-risk-pill strong {
  font-size: 0.82rem;
}

.journey-risk-pill--danger {
  color: #a73522;
  background: rgba(203, 97, 69, 0.12);
  border: 1px solid rgba(203, 97, 69, 0.24);
}

.journey-risk-pill--review {
  color: #a9650f;
  background: rgba(210, 140, 40, 0.13);
  border: 1px solid rgba(210, 140, 40, 0.24);
}

.journey-risk-pill--timeout {
  color: var(--brand);
  background: rgba(41, 107, 115, 0.1);
  border: 1px solid rgba(41, 107, 115, 0.2);
}

.qc-event-section {
  display: grid;
  gap: 7px;
}

.qc-event-section__head,
.qc-event-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.qc-event-section__head strong {
  font-size: 0.78rem;
  color: var(--ink-soft);
}

.qc-event-card {
  padding: 10px;
  border-radius: var(--radius-md);
  border: 1px solid var(--line);
  background: rgba(255, 250, 244, 0.66);
}

.qc-event-card.is-red {
  border-color: rgba(203, 97, 69, 0.42);
  background: rgba(255, 248, 244, 0.76);
}

.qc-event-card.is-yellow {
  border-color: rgba(210, 140, 40, 0.38);
  background: rgba(255, 251, 241, 0.76);
}

.qc-event-card.is-neutral {
  border-color: rgba(41, 107, 115, 0.18);
}

.qc-event-card h4 {
  margin: 0;
  font-size: 0.8rem;
  line-height: 1.25;
}

.qc-event-card__title {
  display: flex;
  align-items: center;
  gap: 7px;
  flex: 1 1 auto;
  min-width: 0;
}

.qc-event-card__title h4 {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.qc-event-card__title span {
  flex: 0 0 auto;
  color: var(--ink-soft);
  font-size: 0.66rem;
  font-weight: 800;
}

.qc-event-card__badges {
  display: flex;
  align-items: center;
  gap: 5px;
  flex: 0 0 auto;
}

.qc-severity-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 23px;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 0.64rem;
  font-weight: 800;
  white-space: nowrap;
}

.qc-confidence-badge {
  display: inline-flex;
  align-items: center;
  min-height: 23px;
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(41, 107, 115, 0.1);
  color: var(--brand);
  font-size: 0.64rem;
  font-weight: 800;
  white-space: nowrap;
}

.qc-severity-badge.is-red {
  color: #a73522;
  background: rgba(203, 97, 69, 0.14);
  border: 1px solid rgba(203, 97, 69, 0.28);
}

.qc-severity-badge.is-yellow {
  color: #a9650f;
  background: rgba(210, 140, 40, 0.14);
  border: 1px solid rgba(210, 140, 40, 0.28);
}

.qc-severity-badge.is-neutral {
  color: var(--brand);
  background: rgba(41, 107, 115, 0.1);
  border: 1px solid rgba(41, 107, 115, 0.18);
}

.qc-event-card p,
.qc-event-main p {
  margin: 6px 0 0;
  font-size: 0.7rem;
  color: var(--ink-soft);
  line-height: 1.38;
}

.qc-event-compact {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(190px, 0.92fr);
  gap: 8px;
  margin-top: 8px;
}

.qc-event-main,
.qc-event-quote {
  display: grid;
  align-content: start;
  gap: 4px;
  min-width: 0;
}

.qc-event-main > span,
.qc-event-quote > span {
  font-size: 0.68rem;
  font-weight: 800;
  color: var(--ink-soft);
  letter-spacing: 0.03em;
}

.qc-event-main p {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.qc-evidence-list {
  display: grid;
  gap: 6px;
}

.qc-evidence {
  display: grid;
  gap: 5px;
}

.qc-evidence-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 9px;
  margin-top: 8px;
  padding-top: 7px;
  border-top: 1px solid rgba(29, 47, 44, 0.07);
  font-size: 0.69rem;
  color: var(--ink-soft);
}

.qc-evidence-meta span {
  min-width: 0;
}

.qc-event-card blockquote {
  margin: 0;
  padding: 7px 9px;
  border-left: 3px solid var(--brand);
  background: rgba(41, 107, 115, 0.06);
  color: var(--ink);
  font-size: 0.73rem;
  line-height: 1.38;
}

.qc-event-card.is-red blockquote {
  border-left-color: var(--danger);
  background: rgba(203, 97, 69, 0.08);
}

.qc-event-card.is-yellow blockquote {
  border-left-color: var(--warn);
  background: rgba(210, 140, 40, 0.08);
}

.qc-trace {
  margin-top: 5px;
  font-size: 0.68rem;
  color: var(--ink-soft);
}

.qc-trace summary {
  cursor: pointer;
  user-select: none;
}

.qc-trace__items {
  display: grid;
  gap: 3px;
  margin-top: 6px;
  padding: 7px 9px;
  border-radius: 8px;
  background: rgba(29, 47, 44, 0.04);
  word-break: break-all;
}

@media (max-width: 860px) {
  .dash-panel--journey {
    max-height: none;
    overflow: visible;
  }

  .journey-panel__body {
    overflow: visible;
    padding-right: 0;
  }

  .dash-panel__header {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .dash-meta {
    flex-wrap: wrap;
  }

  .dash-focus-card__top {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .dash-focus-card__grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .journey-metrics__core {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .dash-coach-card p {
    -webkit-line-clamp: 3;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .dash-chip-row {
    flex-wrap: wrap;
  }

  .dash-milestones {
    max-height: none;
    overflow: visible;
    padding-left: 0;
  }

  .milestone-item__top {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .dash-chip-row {
    flex-wrap: wrap;
  }
}

.event-chain-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  color: var(--dash-fg-muted, #6b8a8e);
  font-size: 13px;
}

.event-chain-loading__spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(41, 107, 115, 0.2);
  border-top-color: rgba(41, 107, 115, 0.8);
  border-radius: 50%;
  animation: ec-spin 0.8s linear infinite;
}

@keyframes ec-spin {
  to { transform: rotate(360deg); }
}

.event-timeline {
  display: grid;
  gap: 8px;
  margin-top: 10px;
  padding: 10px 12px 12px;
  border: 1px solid rgba(29, 47, 44, 0.07);
  border-radius: 16px;
  background: rgba(255, 252, 247, 0.72);
}

.event-timeline__summary {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
}

.event-timeline__summary h3 {
  margin: 4px 0 0;
  color: var(--ink);
  font-size: 0.9rem;
  line-height: 1.2;
}

.event-timeline__count {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  min-height: 28px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(41, 107, 115, 0.1);
  color: var(--brand);
  font-size: 0.68rem;
  font-weight: 900;
}

.event-timeline__list {
  position: relative;
  display: grid;
  gap: 8px;
  padding-left: 0;
}

.event-timeline__list::before {
  position: absolute;
  left: 36px;
  top: 14px;
  bottom: 14px;
  width: 2px;
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(41, 107, 115, 0.06), rgba(41, 107, 115, 0.18), rgba(41, 107, 115, 0.05));
  content: "";
}

.event-timeline__item {
  position: relative;
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr);
  gap: 10px;
  min-height: 0;
  padding: 6px 0;
  border-bottom: 1px solid rgba(29, 47, 44, 0.08);
  background: transparent;
  align-items: center;
}

.event-timeline__item:last-child {
  border-bottom: 0;
}

.event-timeline__rail {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 0;
}

.event-timeline__glyph {
  display: inline-flex;
  width: 38px;
  height: 38px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid color-mix(in srgb, var(--tone) 34%, rgba(29, 47, 44, 0.08));
  background: rgba(255, 252, 247, 0.98);
  color: color-mix(in srgb, var(--tone) 84%, var(--ink));
  box-shadow:
    0 0 0 5px rgba(255, 252, 247, 0.96),
    0 8px 18px color-mix(in srgb, var(--tone) 14%, transparent);
}

.event-timeline__icon {
  width: 18px;
  height: 18px;
  filter: drop-shadow(0 1px 0 rgba(255, 255, 255, 0.72));
}

.event-timeline__content {
  position: relative;
  z-index: 1;
  overflow: visible;
  min-width: 0;
  padding: 9px 12px 10px 14px;
  border: 1px solid rgba(29, 47, 44, 0.07);
  border-left: 3px solid color-mix(in srgb, var(--tone) 54%, rgba(29, 47, 44, 0.12));
  border-radius: 14px;
  background: rgba(255, 250, 244, 0.78);
}

.event-timeline__content h4 {
  font-size: 0.8rem;
  font-weight: 900;
  color: var(--ink);
  margin: 0;
  line-height: 1.22;
}

.event-timeline__content p {
  display: -webkit-box;
  margin: 4px 0 0;
  max-width: none;
  overflow: hidden;
  color: var(--ink-soft);
  font-size: 0.68rem;
  line-height: 1.34;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.event-timeline__head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 5px;
  overflow: visible;
}

.event-timeline__list,
.event-timeline__item {
  overflow: visible;
}

.event-timeline__item--source-active {
  position: relative;
  z-index: 30;
}

.event-timeline__item--source-active .event-timeline__content {
  z-index: 31;
}

.source-picker-zone {
  position: relative;
  display: inline-flex;
  z-index: 40;
}

.source-picker__trigger,
.source-picker__close,
.call-detail-modal__close {
  border: 0;
  cursor: pointer;
  font: inherit;
}

.source-picker__trigger {
  min-height: 20px;
  padding: 2px 7px;
  border: 1px solid rgba(41, 107, 115, 0.22);
  border-radius: 999px;
  background: rgba(41, 107, 115, 0.08);
  color: var(--brand);
  font-size: 0.6rem;
  font-weight: 800;
}

.source-picker__trigger:hover,
.source-picker__trigger:focus-visible {
  background: rgba(41, 107, 115, 0.16);
  outline: none;
}

.source-picker {
  position: fixed;
  z-index: 1000;
  transform: translateY(-50%);
  display: grid;
  width: min(320px, calc(100vw - 32px));
  max-height: min(320px, 55vh);
  overflow-y: auto;
  padding: 7px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: rgba(255, 252, 247, 0.99);
  box-shadow: 0 14px 30px rgba(29, 47, 44, 0.18);
}

.source-picker__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 2px 3px 6px;
  color: var(--ink);
  font-size: 0.7rem;
}

.source-picker__close,
.call-detail-modal__close {
  display: inline-grid;
  width: 26px;
  height: 26px;
  place-items: center;
  border-radius: 50%;
  background: transparent;
  color: var(--ink-soft);
  font-size: 1.15rem;
  line-height: 1;
}

.source-picker__close:hover,
.source-picker__close:focus-visible,
.call-detail-modal__close:hover,
.call-detail-modal__close:focus-visible {
  background: rgba(29, 47, 44, 0.08);
  color: var(--ink);
  outline: none;
}

.source-picker__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) max-content;
  gap: 3px 7px;
  width: 100%;
  padding: 7px;
  border: 0;
  border-top: 1px solid rgba(29, 47, 44, 0.08);
  background: transparent;
  color: var(--ink);
  cursor: pointer;
  font: inherit;
  text-align: left;
}

.source-picker__row:hover,
.source-picker__row:focus-visible {
  background: rgba(41, 107, 115, 0.08);
  outline: none;
}

.source-picker__kind {
  color: var(--brand);
  font-size: 0.64rem;
  font-weight: 900;
}

.source-picker__time {
  color: var(--ink-soft);
  font-size: 0.62rem;
  text-align: right;
  white-space: nowrap;
}

.source-picker__detail {
  grid-column: 1 / -1;
  overflow: hidden;
  color: var(--ink-soft);
  font-size: 0.66rem;
  line-height: 1.35;
  overflow-wrap: anywhere;
  white-space: normal;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

.call-detail-modal {
  position: fixed;
  z-index: 50;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 16px;
  background: rgba(29, 47, 44, 0.36);
}

.call-detail-modal__content {
  width: min(680px, 100%);
  max-height: min(88vh, 760px);
  overflow: auto;
  padding: 16px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #fffaf4;
  box-shadow: 0 24px 48px rgba(29, 47, 44, 0.26);
}

.call-detail-modal__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.call-detail-modal__head h3 {
  margin: 3px 0 0;
  color: var(--ink);
  font-size: 1rem;
}

.call-detail-modal__list {
  display: grid;
  gap: 8px;
  margin: 16px 0 0;
}

.call-detail-modal__list div {
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr);
  gap: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(29, 47, 44, 0.08);
  font-size: 0.76rem;
}

.call-detail-modal__list dt {
  color: var(--ink-soft);
}

.call-detail-modal__list dd {
  min-width: 0;
  margin: 0;
  overflow-wrap: anywhere;
  color: var(--ink);
  font-weight: 700;
}

.call-detail-modal__notice {
  margin: 12px 0 0;
  color: var(--ink-soft);
  font-size: 0.72rem;
}

.event-timeline__time {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 20px;
  padding: 2px 7px;
  border-radius: 999px;
  background: rgba(255, 252, 247, 0.96);
  border: 1px solid color-mix(in srgb, var(--tone) 16%, rgba(29, 47, 44, 0.08));
  font-size: 0.6rem;
  font-weight: 800;
  color: color-mix(in srgb, var(--tone) 74%, var(--ink));
  text-align: center;
  white-space: nowrap;
}

.event-timeline__type {
  display: inline-flex;
  align-items: center;
  min-height: 20px;
  padding: 2px 7px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--tone) 12%, rgba(255, 252, 247, 0.96));
  color: color-mix(in srgb, var(--tone) 82%, var(--ink));
  font-size: 0.6rem;
  font-weight: 800;
}

.event-timeline__item--risk {
  border-bottom-color: rgba(203, 97, 69, 0.13);
}

.event-timeline__item--risk .event-timeline__content {
  background: rgba(255, 246, 241, 0.62);
  border-color: rgba(203, 97, 69, 0.14);
  border-left-color: rgba(203, 97, 69, 0.74);
}

.event-timeline__item--risk .event-timeline__glyph {
  background: rgba(203, 97, 69, 0.1);
  border-color: rgba(203, 97, 69, 0.34);
}

.event-timeline__item--file .event-timeline__content h4,
.event-timeline__item--risk .event-timeline__content h4 {
  color: color-mix(in srgb, var(--tone) 70%, var(--ink));
}

.event-chain-empty {
  padding: 12px;
  text-align: center;
  font-size: 0.8rem;
  color: var(--ink-soft);
}

@media (max-width: 720px) {
  .event-timeline__summary {
    align-items: flex-start;
    flex-direction: column;
  }

  .event-timeline__item {
    grid-template-columns: 54px minmax(0, 1fr);
    gap: 8px;
  }

  .event-timeline__rail {
    align-items: center;
  }

  .event-timeline__list::before {
    left: 27px;
  }

  .source-picker {
    max-width: calc(100vw - 24px);
    transform: none;
  }
}

.call-detail-modal__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
}

.call-detail-modal__action {
  min-height: 32px;
  padding: 6px 10px;
  border: 1px solid rgba(29, 47, 44, 0.16);
  border-radius: 6px;
  background: #fff;
  color: var(--ink);
  cursor: pointer;
  font-size: 0.72rem;
  font-weight: 700;
}

.call-detail-modal__action:hover:not(:disabled),
.call-detail-modal__action:focus-visible:not(:disabled) {
  border-color: var(--brand);
  color: var(--brand);
}

.call-detail-modal__action:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.call-transcript {
  margin-top: 18px;
}

.call-transcript__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: var(--ink-soft);
  font-size: 0.68rem;
}

.call-transcript__list {
  display: grid;
  gap: 8px;
  max-height: 360px;
  margin-top: 8px;
  overflow: auto;
  padding-right: 4px;
}

.call-transcript__segment,
.call-transcript__raw {
  margin: 8px 0 0;
  padding: 10px;
  border: 1px solid rgba(29, 47, 44, 0.1);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.72);
}

.call-transcript__meta {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  color: var(--ink-soft);
  font-size: 0.68rem;
}

.call-transcript__meta strong {
  color: var(--ink);
}

.call-transcript__segment p,
.call-transcript__raw {
  color: var(--ink);
  font-size: 0.76rem;
  line-height: 1.5;
  white-space: pre-wrap;
}

.call-transcript__segment p {
  margin: 6px 0 0;
}

@media (prefers-reduced-motion: reduce) {
  .source-picker,
  .call-detail-modal {
    scroll-behavior: auto;
  }
}
</style>
